import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import Iconify from 'src/components/Iconify';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from "src/@/components/ui/button";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "src/@/components/ui/popover";
import { TagItem } from 'src/sections/@chats/chat/TagItem';
import { useNavigate } from 'react-router';
import { Checkbox } from 'src/@/components/ui/checkbox';
import { mergeTags } from 'src/utils/chats.utils';
import { ScrollArea } from 'src/@/components/ui/scroll-area';
import v2_actionEditarChat from 'src/actions/v2/chats/v2_actionEditarChat';
import { ButtonLoading } from 'src/@/components/use/ButtonLoading';
import useAuth from 'src/hooks/useAuth';
import { getChatInfoByChatId, getChatsAtendimento, getChatsFinalizados, getChatsGeral, getChatsPendente } from 'src/redux/slices/chat';
import { useSnackbar } from 'notistack';
import { Input } from 'src/@/components/ui/input';
import { isEqual } from 'lodash';


const TagsComponent = React.memo(({
    children,
    sx,
    inicial,
    final,
    changeInicial,
    changeFinal,
    onPesquisar,
    tags,
    chatId,
    chat,
    ...other
}) => {
    const navigate = useNavigate();
    const { listaTags } = useSelector((state) => state.chat);
    const [loadingTags, setLoadingTags] = useState(false);
    const { user } = useAuth();
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();

    const [newTagsChats, setNewTagsChats] = useState(() => mergeTags(listaTags, tags));
    const [searchTerm, setSearchTerm] = useState('');

    const previousTagsRef = useRef(tags);

    const mergeTagsCallback = useCallback((listaTags, tags) => {
        return mergeTags(listaTags, tags);
    }, []);

    const handleCheckboxChange = useCallback((id, value) => {
        setNewTagsChats(prevTags =>
            prevTags.map(tag =>
                tag?._id === id ? { ...tag, checked: value } : tag
            )
        );
    }, []);

    useEffect(() => {
        if (!isEqual(tags, previousTagsRef.current)) {
            setNewTagsChats(mergeTagsCallback(listaTags, tags));
            previousTagsRef.current = tags;
        }
    }, [tags, mergeTagsCallback, listaTags]);

    const filteredTags = useMemo(() => newTagsChats.filter(tag =>
        tag.nome.toLowerCase().includes(searchTerm.toLowerCase())
    ), [newTagsChats, searchTerm]);

    const onEditarTags = useCallback(async (ids) => {
        try {
            let data = {
                scope: { tags: ids },
                action: "update_tags"
            };
            await v2_actionEditarChat(chatId, data);
            setLoadingTags(true);
            if (chat?.statusRoom === "Atendendo") dispatch(getChatsAtendimento(user.clienteId, user.id, user.setorId, 10, false));
            if (chat?.statusRoom === "Geral") dispatch(getChatsGeral(user.clienteId, user.id, user.setorId, 50, false));
            if (chat?.statusRoom === "Pendente") dispatch(getChatsPendente(user.clienteId, user.id, user.setorId, 10, false));
            if (chat?.statusRoom === "Finalizado") dispatch(getChatsFinalizados(user.clienteId, user.id, user.setorId, 10, false));
            dispatch(getChatInfoByChatId(chatId, null, user.id, false));
            enqueueSnackbar('Tag atualizada com sucesso', { variant: 'success', autoHideDuration: 1500, anchorOrigin: { vertical: 'top', horizontal: 'right' } });
            setTimeout(() => {
                setLoadingTags(false);
            }, 900);
        } catch (error) {
            setLoadingTags(false);
            console.log(error);
        }
    }, [chatId, chat, dispatch, enqueueSnackbar, user, newTagsChats]);

    const selectedCount = useMemo(() =>
        newTagsChats.filter(t => t.checked).length
    , [newTagsChats]);

    return (
        <div className="mt-1">
            <div className="flex items-center gap-1">
                {tags?.length > 0 && (
                    <div className="flex items-center gap-1">
                        {tags.slice(0, 3).map((tag, index) => (
                            <TagItem
                                key={index}
                                nomeTag={tag.nome}
                                corHex={tag.cor_hex}
                            />
                        ))}
                    </div>
                )}

                <Popover>
                    <PopoverTrigger asChild>
                        <button className="inline-flex items-center gap-0.5 h-[22px] px-1.5 rounded-md border border-dashed border-gray-300 bg-white text-gray-500 hover:border-[#7C3AED] hover:text-[#7C3AED] hover:bg-violet-50 transition-all text-[10px] font-medium">
                            <Iconify icon="eva:pricetags-outline" width={12} />
                            {tags.length > 3 && (
                                <span className="text-[10px]">+{tags.length - 3}</span>
                            )}
                        </button>
                    </PopoverTrigger>

                    <PopoverContent className="w-72 p-0 ml-5 rounded-xl border border-gray-100 shadow-lg" align="start">
                        {/* Header */}
                        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                            <div className="flex items-center gap-2">
                                <div className="w-6 h-6 rounded-lg bg-violet-50 flex items-center justify-center">
                                    <Iconify icon="eva:pricetags-fill" width={14} className="text-[#7C3AED]" />
                                </div>
                                <span className="text-sm font-semibold text-gray-900">Etiquetas</span>
                            </div>
                            <button
                                onClick={() => navigate('/dashboard/configuracoes/tags')}
                                className="text-[11px] font-medium text-[#7C3AED] hover:text-[#6d28d9] transition-colors"
                            >
                                + Nova
                            </button>
                        </div>

                        {/* Search */}
                        <div className="px-3 pt-3 pb-2">
                            <div className="relative">
                                <Iconify
                                    icon="eva:search-outline"
                                    width={15}
                                    className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400"
                                />
                                <Input
                                    type="text"
                                    placeholder="Buscar etiqueta..."
                                    value={searchTerm}
                                    onChange={e => setSearchTerm(e.target.value)}
                                    className="w-full pl-8 h-8 text-xs rounded-lg border-gray-200 focus:border-[#7C3AED] focus:ring-[#7C3AED]/20"
                                />
                            </div>
                        </div>

                        {/* Tags List */}
                        <ScrollArea className="h-48 px-3">
                            <div className="space-y-0.5">
                                {filteredTags?.map((tag) => (
                                    <label
                                        key={tag._id}
                                        htmlFor={`tag-${tag._id}`}
                                        className="flex items-center gap-2.5 px-2 py-1.5 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors group"
                                    >
                                        <Checkbox
                                            id={`tag-${tag._id}`}
                                            checked={tag.checked}
                                            onCheckedChange={value => handleCheckboxChange(tag._id, value)}
                                            className="data-[state=checked]:bg-[#7C3AED] data-[state=checked]:border-[#7C3AED]"
                                        />
                                        <div className="flex items-center gap-1.5 min-w-0 flex-1">
                                            <span className={`w-2 h-2 rounded-full shrink-0 ${tag.cor_hex ? `bg-${tag.cor_hex}-400` : 'bg-gray-400'}`} />
                                            <span className="text-xs text-gray-700 truncate capitalize">
                                                {tag.nome}
                                            </span>
                                        </div>
                                    </label>
                                ))}
                                {filteredTags?.length === 0 && (
                                    <div className="py-6 text-center">
                                        <Iconify icon="eva:search-outline" width={20} className="text-gray-300 mx-auto mb-1" />
                                        <p className="text-xs text-gray-400">Nenhuma etiqueta encontrada</p>
                                    </div>
                                )}
                            </div>
                        </ScrollArea>

                        {/* Footer */}
                        <div className="px-3 py-2.5 border-t border-gray-100 bg-gray-50/50">
                            <ButtonLoading
                                loading={loadingTags}
                                className="w-full h-8 bg-[#7C3AED] hover:bg-[#6d28d9] text-white text-xs font-medium rounded-lg transition-all shadow-sm shadow-[#7C3AED]/20"
                                onClick={() => {
                                    onEditarTags(newTagsChats
                                        .filter(tag => tag.checked)
                                        .map(tag => tag._id));
                                }}
                            >
                                Salvar {selectedCount > 0 && `(${selectedCount})`}
                            </ButtonLoading>
                        </div>
                    </PopoverContent>
                </Popover>
            </div>
        </div>
    );
});

export default TagsComponent;
